import { format } from '@pf/money';
import { keyBetween, newUuidv7 } from '@pf/sync-rules';
import { useParams } from '@tanstack/react-router';
import { ImagePlus, SquarePen } from 'lucide-react';
import { useRef, useState } from 'react';
import { type Me, useMe, useTrip } from '../app/data.ts';
import { Markdown, NoteEditor } from '../app/Notes.tsx';
import { cx } from '../kit/cx.ts';
import { Button, DataTag, DateFlag } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { attach } from '../model/attachments.ts';
import { amountIn, type Day, flag, myShare, type TripModel } from '../model/trip.ts';
import type { Edit } from '../sync.ts';
import s from './Journal.module.css';

/** The trip journal, as on the apps: each day as it happened (visited, moved, spent), a written
 *  entry (a day note titled "Journal") and the day's photos. */
export function Journal() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/journal' });
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const [notice, setNotice] = useState<string | null>(null);
  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model || !me.data) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const run = async (edits: Edit[]) => {
    const refused = await save(edits);
    setNotice(refused > 0 ? "A change wasn't saved; the screen shows what the server has." : null);
  };
  return (
    <div className={s.page}>
      <header className={s.top}>
        <div className={cx(t.readoutS, s.muted)}>{`// ${model.title}`}</div>
        <h1 className={cx(t.displayL, s.flat)}>Journal</h1>
      </header>
      {notice && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {notice}
        </p>
      )}
      {model.days.length === 0 && <p className={cx(s.pad, t.bodyS, s.muted)}>The trip has no dates yet.</p>}
      <ol className={s.days}>
        {model.days.map((d) => (
          <JournalDay key={d.id} day={d} model={model} me={me.data} run={run} />
        ))}
      </ol>
    </div>
  );
}

function JournalDay({
  day,
  model,
  me,
  run,
}: {
  day: Day;
  model: TripModel;
  me: Me;
  run: (e: Edit[]) => Promise<void>;
}) {
  const [writing, setWriting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const userId = me.user.id;
  const home = me.user.homeCurrency;
  const note = model.notes.find(
    (n) => n.targetType === 'day' && n.targetId === day.id && n.title === 'Journal',
  );
  const visits = model.visits.filter((v) => v.dateLocal === day.date && v.userId === userId);
  const titles = visits.flatMap((v) => {
    for (const p of day.items) if (p.item.id === v.itemId) return [p.item.title];
    return [];
  });
  const moved = model.segments
    .filter((x) => x.dateLocal === day.date && x.userId === userId)
    .reduce((a, x) => a + x.distanceM, 0);
  const spent = model.entries
    .filter((e) => e.dateLocal === day.date)
    .reduce((a, e) => a + (amountIn(e, home, myShare(e, userId)) ?? 0), 0);
  const photos = model.attachments.filter((a) => a.ownerType === 'day' && a.ownerId === day.id);

  const add = async (files: FileList) => {
    setUploading(true);
    try {
      for (const file of Array.from(files))
        await attach(run, { tripId: model.id, ownerType: 'day', ownerId: day.id, me: userId }, file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <li className={s.day}>
      <div className={s.dayHead}>
        <DateFlag text={flag(day.date)} />
        <h2 className={cx(t.label, s.flat)}>
          Day {String(day.number).padStart(2, '0')} / {String(day.count).padStart(2, '0')}
        </h2>
        {day.destination && <span className={cx(t.label, s.faint)}>{`// ${day.destination.name}`}</span>}
      </div>
      <div className={s.tags}>
        {visits.length > 0 && <DataTag label="Visited" value={String(visits.length)} />}
        {moved > 0 && <DataTag label="Moved" value={`${(moved / 1000).toFixed(1)} km`} />}
        {spent > 0 && <DataTag label="Spent" value={format(spent, home)} />}
      </div>
      {titles.length > 0 && <p className={cx(t.caption, s.muted, s.flat)}>{titles.join(' · ')}</p>}
      {writing ? (
        <NoteEditor
          model={model}
          me={me}
          fixedTitle="Journal"
          {...(note ? { note } : {})}
          onCancel={() => setWriting(false)}
          onSave={async (_title, body) => {
            const edit: Edit = note
              ? {
                  table: 'note',
                  id: note.id,
                  tripId: model.id,
                  ownerUserId: note.ownerUserId,
                  fields: { title: 'Journal', body },
                }
              : {
                  table: 'note',
                  id: newUuidv7(),
                  tripId: model.id,
                  fields: {
                    targetType: 'day',
                    targetId: day.id,
                    title: 'Journal',
                    body,
                    sortKey: keyBetween(null, null),
                  },
                };
            await run([edit]);
            setWriting(false);
          }}
        />
      ) : (
        <>
          {note?.body && (
            <Markdown
              text={note.body}
              model={model}
              onToggle={(body) =>
                void run([
                  {
                    table: 'note',
                    id: note.id,
                    tripId: model.id,
                    ownerUserId: note.ownerUserId,
                    fields: { body },
                  },
                ])
              }
            />
          )}
          <div className={s.actions}>
            <Button variant="ghost" size="small" icon={SquarePen} onClick={() => setWriting(true)}>
              {note ? 'Edit the entry' : 'Write about this day'}
            </Button>
            <input
              ref={input}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files?.length) void add(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="small"
              icon={ImagePlus}
              disabled={uploading}
              onClick={() => input.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Add photos'}
            </Button>
          </div>
        </>
      )}
      {photos.length > 0 && (
        <ul className={s.photos}>
          {photos.map((a) => (
            <li key={a.id}>
              {a.uploadedAt ? (
                <a href={`/v1/attachments/${a.id}`} target="_blank" rel="noreferrer">
                  {a.mime?.startsWith('image/') ? (
                    <img src={`/v1/attachments/${a.id}`} alt={a.filename} loading="lazy" />
                  ) : (
                    <span className={t.caption}>{a.filename}</span>
                  )}
                </a>
              ) : (
                <span className={cx(t.caption, s.muted, s.pending)}>
                  {a.filename} · uploading on another device
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
